/**
 * Hook for responsive sidebar collapse behavior.
 *
 * Auto-collapses sidebar when window width falls below threshold.
 * Restores to user preference when window width exceeds threshold.
 * Returns isWindowNarrow for UI feedback (e.g., denied animation).
 * Returns canExpand for resize handle feedback (e.g., red glow).
 */

import { useEffect, useState } from "react";

import { SIDEBAR_MIN_WIDTH, useSidebarStore } from "./store";

export const COLLAPSE_THRESHOLD = 800;
const MIN_CONTENT_WIDTH = 400;

export function useResponsiveSidebar() {
  const { userPreference, isCollapsed, setAutoCollapsed } = useSidebarStore();
  const [isWindowNarrow, setIsWindowNarrow] = useState(() =>
    typeof window !== "undefined"
      ? window.innerWidth < COLLAPSE_THRESHOLD
      : false
  );
  const [canExpand, setCanExpand] = useState(() =>
    typeof window !== "undefined"
      ? window.innerWidth >= SIDEBAR_MIN_WIDTH + MIN_CONTENT_WIDTH
      : true
  );

  useEffect(() => {
    function handleResize() {
      const isNarrow = window.innerWidth < COLLAPSE_THRESHOLD;
      const canExpandNow =
        window.innerWidth >= SIDEBAR_MIN_WIDTH + MIN_CONTENT_WIDTH;

      setIsWindowNarrow(isNarrow);
      setCanExpand(canExpandNow);

      if (isNarrow && !isCollapsed) {
        setAutoCollapsed(true);
      } else if (!isNarrow) {
        setAutoCollapsed(userPreference);
      }
    }

    handleResize();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [userPreference, isCollapsed, setAutoCollapsed]);

  return { isWindowNarrow, canExpand };
}
